import { IAppContext } from 'App/AppContext';
import { IHubConnection, startHubConnection } from './GeneralHub';

//
// The Copilot hub of the API (/hubs/copilot), ported from ISOPlanner. The API pushes the agent events of a run to
// the one connection whose id the run request carried (POST executions?connectionId=...), after checking that the
// connection belongs to the caller.
//
export const ReceiveAgentEvent = 'ReceiveAgentEvent';

export type CopilotHubMethodReceiveAgentEvent = (event: CopilotAgentEvent) => void;

// The decision on an approval of the signed-in user's own run, sent to every connection of the requester on the API
// instance that recorded it
export const ReceiveApprovalEvent = 'ReceiveApprovalEvent';

export type CopilotHubMethodReceiveApprovalEvent = (event: CopilotApprovalEvent) => void;

// The approval queue of the signed-in user changed (a request naming them as approver was opened, decided, expired or
// withdrawn), sent to every connection of each resolved approver on the API instance that made the change
export const ReceiveApprovalQueueChanged = 'ReceiveApprovalQueueChanged';

export type CopilotHubMethodReceiveApprovalQueueChanged = (event: CopilotApprovalQueueEvent) => void;

export const CopilotHubEndpoint = 'hubs/copilot';

export enum CopilotHubMessageType {
  Chat = 0,
  Document = 1,
}

export enum CopilotAgentEventKind {
  TextDelta = 0,
  ToolResult = 2,
  Trace = 3,
  Done = 4,
  Error = 5,
}

// The tool that stores a document; its ToolResult payload carries the document id, file name and title
export const CreateDocumentToolName = 'create_document';

// The tool that stores a chart (phase 23): a document too, announced the same way
export const ChartRenderToolName = 'chart_render';

// A file the model produced itself, landed as a document of the run (phase 57); announced the same way
export const ModelOutputToolName = 'model_output';

// the tools whose ToolResult event names a stored document
export const isDocumentToolName = (toolName: string | undefined): boolean =>
  toolName === CreateDocumentToolName || toolName === ChartRenderToolName || toolName === ModelOutputToolName;

export interface CopilotDocumentToolPayload {
  documentId?: string;
  fileName?: string;
  title?: string;
}

export interface CopilotAgentEvent {
  runId?: string;
  sequence?: number;
  parentId?: string;
  phase?: string;
  iteration?: number;
  status?: string;
  clientSequence?: number;
  kind: CopilotAgentEventKind;
  text?: string;
  messageType?: CopilotHubMessageType;
  callId?: string;
  toolName?: string;
  argumentsJson?: string;
  resultPreview?: string;
  payload?: unknown;
  traceName?: string;
  occurredAt?: string;
  errorCode?: string;
  message?: string;
}

// A signal, not the record: the client reads the execution again (GET executions/{id}). With status Approved the
// execution is queued again and resumes on the API's worker without a stream.
export interface CopilotApprovalEvent {
  approvalRequestId: string;
  executionId: string;
  // ApprovalRequestStatus: 1 Approved, 2 Rejected, 3 Expired
  status: number;
  // undefined for an expiry
  deciderName?: string;
  comment?: string;
  decidedAt?: string;
}

// A signal for the menu badge only: the client reads GET approvals/mine again. Nothing of the run is in it.
export interface CopilotApprovalQueueEvent {
  approvalRequestId: string;
  // ApprovalRequestStatus: 0 Pending (a new request), 1 Approved, 2 Rejected, 3 Expired, 4 Withdrawn
  status: number;
}

export const getCopilotDocumentToolPayload = (event: CopilotAgentEvent): CopilotDocumentToolPayload | undefined => {
  if (!event.payload || typeof event.payload !== 'object') return undefined;

  return event.payload as CopilotDocumentToolPayload;
};

// A create_document (or chart_render) call arrives as two ToolResult events with the same callId: the tool's own,
// whose payload names the stored document, and the runner's generic one. Only the first counts as the document; the
// generic one is a duplicate of the same step and is dropped from the activity list too.
export const isCopilotDocumentToolEvent = (event: CopilotAgentEvent): boolean =>
  event.kind === CopilotAgentEventKind.ToolResult &&
  isDocumentToolName(event.toolName) &&
  !!getCopilotDocumentToolPayload(event)?.documentId;

export const isCopilotDuplicateDocumentToolEvent = (event: CopilotAgentEvent): boolean =>
  event.kind === CopilotAgentEventKind.ToolResult &&
  isDocumentToolName(event.toolName) &&
  !getCopilotDocumentToolPayload(event)?.documentId;

export const startCopilotHubConnection = async (
  appContext: IAppContext,
  agentEventCallback: CopilotHubMethodReceiveAgentEvent,
  approvalEventCallback: CopilotHubMethodReceiveApprovalEvent,
): Promise<IHubConnection | null> => {
  return startHubConnection(appContext, CopilotHubEndpoint, [
    { method: ReceiveAgentEvent, callback: agentEventCallback },
    { method: ReceiveApprovalEvent, callback: approvalEventCallback },
  ]);
};

// The connection of the app shell that keeps the approvals badge current (usePendingApprovalsPoller). It listens for
// queue changes only; the conversation scene keeps its own connection for the runs (startCopilotHubConnection).
export const startCopilotApprovalQueueConnection = async (
  appContext: IAppContext,
  queueChangedCallback: CopilotHubMethodReceiveApprovalQueueChanged,
): Promise<IHubConnection | null> => {
  return startHubConnection(appContext, CopilotHubEndpoint, [
    { method: ReceiveApprovalQueueChanged, callback: queueChangedCallback },
  ]);
};
